import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { urls } from './config.dev';
import JSEncrypt from 'jsencrypt';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEye, 
  faEyeSlash,
  faSpinner,
  faCheckCircle,
  faUserPlus,
  faBuilding,
  faLock,
  faMapMarkerAlt,
  faCalendarAlt,
  faUsers,
  faUserTag,
  faHome,
  faPhoneAlt,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import 'react-toastify/dist/ReactToastify.css';

const publicKey = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAup3FU135mAvJT6OheYW3
pQyWf6jvS4duUMY4cXrlJXyGqu8HqvTU0ewPy6w2HhCPxWboNclkAkPhOCc4URNT
x1Grg+mCsWmfhVimP2wtfmlBCJ09cyDMYf93iGj8RFf3CshY5yhppT/pX+RgTuXw
ClpOXe24CLG2VF9suNylk+ReAMLyOxaekYofAMBvvrD4+GYPJgvkTMXCXCKp2PnO
8+OjiltNMnoyqPEZoXHTV4EXtTrjYnwzSe0WZSSuzgVMhmtdx+IS4eisSumHV1eI
wBeZwI0bYGxDCedPRassmSFgTFqkkcgIXmEP1n5w/08S/QPr2G+myKTeRqp5RJA5
PQIDAQAB
-----END PUBLIC KEY-----`;

function ClinicRegistration() {
  const [formData, setFormData] = useState({
    clinicName: '',
    district: '',
    county: '',
    subCounty: '',
    town: '',
    yearOfOpening: '',
    numberOfEmployees: '',
    ownersNames: '',
    ownersAddress: '',
    ownersContact: '',
    ownersWhatsapp: '',
    referrer: '',
    password: '',
    confirmPassword: '',
  });

  const [loading, setLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  // ── Detect mobile device ──
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = typeof window.navigator === 'undefined' ? '' : navigator.userAgent;
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      setIsMobile(mobile);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ── Set theme color for browser on mobile (white status bar) ──
  useEffect(() => {
    if (isMobile) {
      // Set the theme-color meta tag for mobile browsers
      let metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (!metaThemeColor) {
        metaThemeColor = document.createElement('meta');
        metaThemeColor.setAttribute('name', 'theme-color');
        document.head.appendChild(metaThemeColor);
      }
      metaThemeColor.setAttribute('content', '#ffffff');
      
      // Also set the status bar style for iOS
      let metaStatusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
      if (!metaStatusBar) {
        metaStatusBar = document.createElement('meta');
        metaStatusBar.setAttribute('name', 'apple-mobile-web-app-status-bar-style');
        document.head.appendChild(metaStatusBar);
      }
      metaStatusBar.setAttribute('content', 'black-translucent');
    }
  }, [isMobile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const requiredFields = [
      'clinicName',
      'district',
      'county',
      'town',
      'yearOfOpening',
      'numberOfEmployees',
      'ownersNames',
      'ownersAddress',
      'ownersContact',
      'ownersWhatsapp',
      'password',
      'confirmPassword'
    ];
  
    for (const field of requiredFields) {
      if (formData[field].trim() === '') {
        toast.error('Please fill in all required fields.', { position: 'top-center' });
        return;
      }
    }
  
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match!', { position: 'top-center' });
      return;
    }
  
    const encrypt = new JSEncrypt();
    encrypt.setPublicKey(publicKey);
    const encryptedPassword = encrypt.encrypt(formData.password);
  
    const encryptedFormData = { ...formData, password: encryptedPassword, confirmPassword: encryptedPassword };
  
    setLoading(true);
    try {
      const response = await fetch(urls.registerClinic, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(encryptedFormData),
      });
  
      if (response.ok) {
        const responseData = await response.json();
        toast.success('Registration successful! Please wait as we redirect you to the login page.', { position: 'top-center' });
        
        setTimeout(() => {
          navigate('/login');
        }, 5000);
        
      } else {
        const responseData = await response.json();
        if (responseData.error === "Clinic name already exists.") {
          toast.error('Business Name is already taken, please choose another name.', { position: 'top-center' });
        } else {
          toast.error('Registration failed!', { position: 'top-center' });
        }
      }
    } catch (error) {
      toast.error('Registration failed!', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      backgroundColor: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      padding: '20px',
      boxSizing: 'border-box',
    }}>
      <ToastContainer 
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Main Card Container */}
      <div style={{
        maxWidth: '680px',
        width: '100%',
        backgroundColor: '#ffffff',
        borderRadius: '24px',
        boxShadow: '0 20px 35px -10px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.01)',
        padding: '32px 28px',
        transition: 'all 0.3s ease',
        border: '1px solid #f0f0f0',
      }}>
        
        {/* Header Section */}
        <div style={{ marginBottom: '32px', textAlign: 'center' }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: 'linear-gradient(135deg, #007CF0 0%, #0068FF 100%)',
            borderRadius: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            boxShadow: '0 10px 20px -5px rgba(0, 124, 240, 0.2)',
          }}>
            <FontAwesomeIcon icon={faUserPlus} size="xl" style={{ color: 'white' }} />
          </div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            margin: '0 0 8px',
            letterSpacing: '-0.3px',
            color: '#1a1a1a',
          }}>Create Account</h1>
          <p style={{
            fontSize: '15px',
            fontWeight: '400',
            margin: '0',
            color: '#666666',
          }}>Register your business for comprehensive management</p>
        </div>

        {/* Registration Form */}
        <form onSubmit={handleSubmit}>
          {/* Row 1: Business Name & District */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#333333',
                marginBottom: '8px',
              }}>
                <FontAwesomeIcon icon={faBuilding} style={{ marginRight: '6px', color: '#007CF0', fontSize: '12px' }} /> 
                Business Name
              </label>
              <input
                type="text"
                name="clinicName"
                value={formData.clinicName}
                onChange={handleChange}
                required
                placeholder="Enter business name"
                style={{
                  padding: '12px 14px',
                  fontSize: '14px',
                  border: '1.5px solid #e8e8e8',
                  borderRadius: '12px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit',
                  backgroundColor: '#ffffff',
                  color: '#1a1a1a',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => e.target.style.borderColor = '#007CF0'}
                onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#333333',
                marginBottom: '8px',
              }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '6px', color: '#007CF0', fontSize: '12px' }} /> 
                District
              </label>
              <input
                type="text"
                name="district"
                value={formData.district}
                onChange={handleChange}
                required
                placeholder="Enter district"
                style={{
                  padding: '12px 14px',
                  fontSize: '14px',
                  border: '1.5px solid #e8e8e8',
                  borderRadius: '12px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit',
                  backgroundColor: '#ffffff',
                  color: '#1a1a1a',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => e.target.style.borderColor = '#007CF0'}
                onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
              />
            </div>
          </div>

          {/* Row 2: Country & Town */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#333333',
                marginBottom: '8px',
              }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '6px', color: '#007CF0', fontSize: '12px' }} /> 
                Country
              </label>
              <input
                type="text"
                name="county"
                value={formData.county}
                onChange={handleChange}
                required
                placeholder="Enter country"
                style={{
                  padding: '12px 14px',
                  fontSize: '14px',
                  border: '1.5px solid #e8e8e8',
                  borderRadius: '12px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit',
                  backgroundColor: '#ffffff',
                  color: '#1a1a1a',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => e.target.style.borderColor = '#007CF0'}
                onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#333333',
                marginBottom: '8px',
              }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '6px', color: '#007CF0', fontSize: '12px' }} /> 
                Town/City
              </label>
              <input
                type="text"
                name="town"
                value={formData.town}
                onChange={handleChange}
                required
                placeholder="Enter town/city"
                style={{
                  padding: '12px 14px',
                  fontSize: '14px',
                  border: '1.5px solid #e8e8e8',
                  borderRadius: '12px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit',
                  backgroundColor: '#ffffff',
                  color: '#1a1a1a',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => e.target.style.borderColor = '#007CF0'}
                onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
              />
            </div>
          </div>

          {/* Row 3: Year & Employees */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#333333',
                marginBottom: '8px',
              }}>
                <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '6px', color: '#007CF0', fontSize: '12px' }} /> 
                Year of Opening
              </label>
              <input
                type="number"
                name="yearOfOpening"
                value={formData.yearOfOpening}
                onChange={handleChange}
                required
                placeholder="e.g., 2020"
                style={{
                  padding: '12px 14px',
                  fontSize: '14px',
                  border: '1.5px solid #e8e8e8',
                  borderRadius: '12px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit',
                  backgroundColor: '#ffffff',
                  color: '#1a1a1a',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => e.target.style.borderColor = '#007CF0'}
                onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#333333',
                marginBottom: '8px',
              }}>
                <FontAwesomeIcon icon={faUsers} style={{ marginRight: '6px', color: '#007CF0', fontSize: '12px' }} /> 
                Number of Employees
              </label>
              <input
                type="number"
                name="numberOfEmployees"
                value={formData.numberOfEmployees}
                onChange={handleChange}
                required
                placeholder="e.g., 10"
                style={{
                  padding: '12px 14px',
                  fontSize: '14px',
                  border: '1.5px solid #e8e8e8',
                  borderRadius: '12px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit',
                  backgroundColor: '#ffffff',
                  color: '#1a1a1a',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => e.target.style.borderColor = '#007CF0'}
                onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
              />
            </div>
          </div>

          {/* Owners Names */}
          <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '16px' }}>
            <label style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#333333',
              marginBottom: '8px',
            }}>
              <FontAwesomeIcon icon={faUserTag} style={{ marginRight: '6px', color: '#007CF0', fontSize: '12px' }} /> 
              Owners' Names
            </label>
            <input
              type="text"
              name="ownersNames"
              value={formData.ownersNames}
              onChange={handleChange}
              required
              placeholder="Enter owners' full names"
              style={{
                padding: '12px 14px',
                fontSize: '14px',
                border: '1.5px solid #e8e8e8',
                borderRadius: '12px',
                outline: 'none',
                transition: 'all 0.2s ease',
                fontFamily: 'inherit',
                backgroundColor: '#ffffff',
                color: '#1a1a1a',
                width: '100%',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = '#007CF0'}
              onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
            />
          </div>

          {/* Owners Address */}
          <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '16px' }}>
            <label style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#333333',
              marginBottom: '8px',
            }}>
              <FontAwesomeIcon icon={faHome} style={{ marginRight: '6px', color: '#007CF0', fontSize: '12px' }} /> 
              Owners' Address
            </label>
            <input
              type="text"
              name="ownersAddress"
              value={formData.ownersAddress}
              onChange={handleChange}
              required
              placeholder="Enter owners' physical address"
              style={{
                padding: '12px 14px',
                fontSize: '14px',
                border: '1.5px solid #e8e8e8',
                borderRadius: '12px',
                outline: 'none',
                transition: 'all 0.2s ease',
                fontFamily: 'inherit',
                backgroundColor: '#ffffff',
                color: '#1a1a1a',
                width: '100%',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = '#007CF0'}
              onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
            />
          </div>

          {/* Contact & WhatsApp */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#333333',
                marginBottom: '8px',
              }}>
                <FontAwesomeIcon icon={faPhoneAlt} style={{ marginRight: '6px', color: '#007CF0', fontSize: '12px' }} /> 
                Contact Number
              </label>
              <input
                type="tel"
                name="ownersContact"
                value={formData.ownersContact}
                onChange={handleChange}
                required
                placeholder="e.g., 0777123456"
                style={{
                  padding: '12px 14px',
                  fontSize: '14px',
                  border: '1.5px solid #e8e8e8',
                  borderRadius: '12px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit',
                  backgroundColor: '#ffffff',
                  color: '#1a1a1a',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => e.target.style.borderColor = '#007CF0'}
                onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#333333',
                marginBottom: '8px',
              }}>
                <FontAwesomeIcon icon={faPhoneAlt} style={{ marginRight: '6px', color: '#25D366', fontSize: '12px' }} /> 
                WhatsApp Number
              </label>
              <input
                type="tel"
                name="ownersWhatsapp"
                value={formData.ownersWhatsapp}
                onChange={handleChange}
                required
                placeholder="e.g., 0777123456"
                style={{
                  padding: '12px 14px',
                  fontSize: '14px',
                  border: '1.5px solid #e8e8e8',
                  borderRadius: '12px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit',
                  backgroundColor: '#ffffff',
                  color: '#1a1a1a',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => e.target.style.borderColor = '#25D366'}
                onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
              />
            </div>
          </div>

          {/* Referrer (Optional) */}
          <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '20px' }}>
            <label style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#333333',
              marginBottom: '8px',
            }}>
              <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '6px', color: '#007CF0', fontSize: '12px' }} /> 
              Referrer (Optional)
            </label>
            <input
              type="text"
              name="referrer"
              value={formData.referrer}
              onChange={handleChange}
              placeholder="Who referred you?"
              style={{
                padding: '12px 14px',
                fontSize: '14px',
                border: '1.5px solid #e8e8e8',
                borderRadius: '12px',
                outline: 'none',
                transition: 'all 0.2s ease',
                fontFamily: 'inherit',
                backgroundColor: '#ffffff',
                color: '#1a1a1a',
                width: '100%',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = '#007CF0'}
              onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
            />
          </div>

          {/* Password & Confirm Password */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '28px' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#333333',
                marginBottom: '8px',
              }}>
                <FontAwesomeIcon icon={faLock} style={{ marginRight: '6px', color: '#007CF0', fontSize: '12px' }} /> 
                Password
              </label>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  type={passwordVisible ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="Create password"
                  style={{
                    padding: '12px 40px 12px 14px',
                    fontSize: '14px',
                    border: '1.5px solid #e8e8e8',
                    borderRadius: '12px',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    fontFamily: 'inherit',
                    backgroundColor: '#ffffff',
                    color: '#1a1a1a',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#007CF0'}
                  onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
                />
                <FontAwesomeIcon
                  icon={passwordVisible ? faEyeSlash : faEye}
                  onClick={() => setPasswordVisible(!passwordVisible)}
                  style={{
                    position: 'absolute',
                    right: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: '#999999',
                    fontSize: '16px',
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#333333',
                marginBottom: '8px',
              }}>
                <FontAwesomeIcon icon={faLock} style={{ marginRight: '6px', color: '#007CF0', fontSize: '12px' }} /> 
                Confirm Password
              </label>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  type={confirmPasswordVisible ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  placeholder="Confirm password"
                  style={{
                    padding: '12px 40px 12px 14px',
                    fontSize: '14px',
                    border: '1.5px solid #e8e8e8',
                    borderRadius: '12px',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    fontFamily: 'inherit',
                    backgroundColor: '#ffffff',
                    color: '#1a1a1a',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#007CF0'}
                  onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
                />
                <FontAwesomeIcon
                  icon={confirmPasswordVisible ? faEyeSlash : faEye}
                  onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                  style={{
                    position: 'absolute',
                    right: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: '#999999',
                    fontSize: '16px',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: '1',
                padding: '14px 24px',
                fontSize: '15px',
                fontWeight: '600',
                color: 'white',
                background: 'linear-gradient(135deg, #007CF0 0%, #0068FF 100%)',
                border: 'none',
                borderRadius: '14px',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: loading ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Registering...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faUserPlus} />
                  <span>Create Account</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => navigate('/login')}
              style={{
                flex: '1',
                padding: '14px 24px',
                fontSize: '15px',
                fontWeight: '600',
                color: '#059669',
                background: '#ffffff',
                border: '1.5px solid #059669',
                borderRadius: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#059669';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
                e.currentTarget.style.color = '#059669';
              }}
            >
              <FontAwesomeIcon icon={faEye} />
              <span>Back to Login</span>
            </button>
          </div>
        </form>

        {/* Security Info */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          marginTop: '32px',
          padding: '16px 0 0 0',
          borderTop: '1px solid #f0f0f0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#555555' }}>
            <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#007CF0', fontSize: '13px' }} />
            <span>Secure & Encrypted Registration</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#555555' }}>
            <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#007CF0', fontSize: '13px' }} />
            <span>Your data is protected with 256-bit encryption</span>
          </div>
        </div>

        {/* Footer Text */}
        <div style={{
          marginTop: '24px',
          textAlign: 'center',
          fontSize: '12px',
          color: '#999999',
        }}>
          © 2025 Business Suite. All rights reserved.
        </div>
      </div>
    </div>
  );
}

export default ClinicRegistration;